export interface FurnitureCatalogCollectionDefinition {
  key: string;
  categoryKey: string;
  name: string;
}

export interface FurnitureCatalogCategoryDefinition {
  key: string;
  name: string;
}

export const DRAFT_LIBRARY_CATEGORY_KEY = "my-creations";
export const DRAFT_LIBRARY_CATEGORY_NAME = "My Creations";
export const DRAFT_LIBRARY_COLLECTION_KEY = "drafts";
export const DRAFT_LIBRARY_COLLECTION_NAME = "Drafts";

export const FURNITURE_CATALOG_CATEGORIES: FurnitureCatalogCategoryDefinition[] = [
  { key: "living", name: "Living Space" },
  { key: "decor", name: "Decor & Art" },
  { key: "utility", name: "Utility & Motion" },
  { key: "structure", name: "Architecture" },
  { key: "themed", name: "Themed Sets" },
  { key: DRAFT_LIBRARY_CATEGORY_KEY, name: DRAFT_LIBRARY_CATEGORY_NAME },
];

// Inspired by classic Habbo/Havana catalogue families (Area, Mode, Lodge, Teleporters, etc.).
export const FURNITURE_CATALOG_COLLECTIONS: FurnitureCatalogCollectionDefinition[] =
  [
    { key: "chairs-sofas", categoryKey: "living", name: "Chairs & Sofas" },
    { key: "tables-counters", categoryKey: "living", name: "Tables & Counters" },
    { key: "beds-lounge", categoryKey: "living", name: "Beds & Lounge" },
    { key: "plants", categoryKey: "decor", name: "Plants" },
    { key: "lighting", categoryKey: "decor", name: "Lighting" },
    { key: "rugs-art", categoryKey: "decor", name: "Rugs & Art" },
    { key: "accessories", categoryKey: "decor", name: "Accessories" },
    { key: "doors", categoryKey: "utility", name: "Doors" },
    { key: "teleporters", categoryKey: "utility", name: "Teleporters" },
    { key: "rollers", categoryKey: "utility", name: "Rollers" },
    { key: "interactive", categoryKey: "utility", name: "Interactive" },
    { key: "floors-walls", categoryKey: "structure", name: "Floors & Walls" },
    { key: "windows", categoryKey: "structure", name: "Windows" },
    { key: "room-shape", categoryKey: "structure", name: "Room Shape" },
    { key: "gothic", categoryKey: "themed", name: "Gothic" },
    { key: "asian", categoryKey: "themed", name: "Asian" },
    { key: "arctic", categoryKey: "themed", name: "Arctic" },
    { key: "seasonal", categoryKey: "themed", name: "Seasonal" },
    { key: "urban", categoryKey: "themed", name: "Urban" },
    {
      key: DRAFT_LIBRARY_COLLECTION_KEY,
      categoryKey: DRAFT_LIBRARY_CATEGORY_KEY,
      name: DRAFT_LIBRARY_COLLECTION_NAME,
    },
  ];

export const DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY = "living";
export const DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY = "chairs-sofas";

const CATEGORY_KEY_SET = new Set(
  FURNITURE_CATALOG_CATEGORIES.map((category) => category.key)
);
const COLLECTION_BY_KEY = new Map(
  FURNITURE_CATALOG_COLLECTIONS.map((collection) => [
    collection.key,
    collection,
  ] as const)
);

function normalizeKey(value: string | undefined | null): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function normalizeCatalogCategoryKey(
  raw: string | undefined | null
): string {
  const normalized = normalizeKey(raw);
  if (CATEGORY_KEY_SET.has(normalized)) {
    return normalized;
  }
  return DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY;
}

export function normalizeCatalogCollectionKey(
  raw: string | undefined | null,
  categoryKey: string | undefined | null
): string {
  const normalizedCategoryKey = normalizeCatalogCategoryKey(categoryKey);
  const normalized = normalizeKey(raw);
  const exact = COLLECTION_BY_KEY.get(normalized);
  if (exact && exact.categoryKey === normalizedCategoryKey) {
    return exact.key;
  }
  const firstForCategory = FURNITURE_CATALOG_COLLECTIONS.find(
    (collection) => collection.categoryKey === normalizedCategoryKey
  );
  if (firstForCategory) {
    return firstForCategory.key;
  }
  return DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY;
}

export function resolveCatalogCategoryName(categoryKey: string): string {
  return (
    FURNITURE_CATALOG_CATEGORIES.find((category) => category.key === categoryKey)
      ?.name ?? "Other"
  );
}

export function resolveCatalogCollectionName(collectionKey: string): string {
  return (
    FURNITURE_CATALOG_COLLECTIONS.find(
      (collection) => collection.key === collectionKey
    )?.name ?? "General"
  );
}

export function listCollectionsForCategory(
  categoryKey: string
): FurnitureCatalogCollectionDefinition[] {
  return FURNITURE_CATALOG_COLLECTIONS.filter(
    (collection) => collection.categoryKey === categoryKey
  );
}
